import React, { useState, useEffect, useCallback } from 'react';
import {
  projectsAllApi,
  projectsPlanningApi,
  projectsTaskTrackerApi,
  type ProjectDto,
  type PlanningItemDto,
  type TaskTrackerItemDto,
  type TaskTrackerColumnDto,
} from '../../services/api';

function getOrCreateDefaultColumns(projectId: string, existingColumns: TaskTrackerColumnDto[]): TaskTrackerColumnDto[] {
  const forProject = existingColumns.filter((c) => c.project_id === projectId);
  if (forProject.length > 0) return forProject.sort((a, b) => a.sort_order - b.sort_order);
  return [];
}

function buildHierarchy(items: PlanningItemDto[], projectId: string): PlanningItemDto[] {
  const filtered = items.filter((i) => i.project_id === projectId);
  const byParent = new Map<string | null, PlanningItemDto[]>();
  for (const item of filtered) {
    const pid = item.parent_id;
    if (!byParent.has(pid)) byParent.set(pid, []);
    byParent.get(pid)!.push(item);
  }
  Array.from(byParent.values()).forEach((arr) => arr.sort((a, b) => a.sort_order - b.sort_order));
  const result: PlanningItemDto[] = [];
  const add = (parentId: string | null) => {
    const children = byParent.get(parentId) || [];
    for (const c of children) {
      result.push(c);
      add(c.id);
    }
  };
  add(null);
  return result;
}

function getItemLevel(item: PlanningItemDto, items: PlanningItemDto[]): number {
  if (!item.parent_id) return 1;
  const parent = items.find((p) => p.id === item.parent_id);
  if (!parent) return 2;
  return parent.parent_id ? 3 : 2;
}

export default function ProjectMonitoringPage() {
  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [planningItems, setPlanningItems] = useState<PlanningItemDto[]>([]);
  const [sprints, setSprints] = useState<{ id: string; project_id: string }[]>([]);
  const [columns, setColumns] = useState<TaskTrackerColumnDto[]>([]);
  const [items, setItems] = useState<TaskTrackerItemDto[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [loading, setLoading] = useState(true);

  const loadProjects = useCallback(async () => {
    setLoading(true);
    try {
      const res = await projectsAllApi.list();
      if (res.data) setProjects(res.data);
      else setProjects([]);
    } catch {
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadProjectData = useCallback(async (projectId: string) => {
    if (!projectId) return;
    try {
      const [planningRes, sprintsRes, columnsRes, itemsRes] = await Promise.all([
        projectsPlanningApi.list(projectId),
        projectsTaskTrackerApi.listSprints(projectId),
        projectsTaskTrackerApi.listColumns(projectId),
        projectsTaskTrackerApi.listItems(projectId),
      ]);
      if (planningRes.data) setPlanningItems(planningRes.data);
      else setPlanningItems([]);
      if (sprintsRes.data) setSprints(sprintsRes.data);
      else setSprints([]);
      if (columnsRes.data) setColumns(columnsRes.data);
      else setColumns([]);
      if (itemsRes.data) setItems(itemsRes.data);
      else setItems([]);
    } catch {
      setPlanningItems([]);
      setSprints([]);
      setColumns([]);
      setItems([]);
    }
  }, []);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    if (selectedProjectId) {
      loadProjectData(selectedProjectId);
    } else {
      setPlanningItems([]);
      setSprints([]);
      setColumns([]);
      setItems([]);
    }
  }, [selectedProjectId, loadProjectData]);

  const selectedProject = projects.find((p) => p.id === selectedProjectId);
  const projectSprintIds = selectedProjectId
    ? new Set(sprints.filter((s) => s.project_id === selectedProjectId).map((s) => s.id))
    : new Set<string>();
  const projectTaskItems = items.filter((i) => projectSprintIds.has(i.sprint_id));
  const projectColumns = selectedProjectId
    ? getOrCreateDefaultColumns(selectedProjectId, columns)
    : [];
  const doneColumn = projectColumns.find(
    (c) => c.name.toLowerCase() === 'done' || c.name.toLowerCase() === 'completed'
  );
  const doneColumnId = doneColumn?.id ?? null;
  const projectDoneCount = doneColumnId
    ? projectTaskItems.filter((i) => i.column_id === doneColumnId).length
    : 0;
  const overallPercent =
    projectTaskItems.length > 0
      ? Math.round((projectDoneCount / projectTaskItems.length) * 100)
      : 0;

  const hierarchy = selectedProjectId ? buildHierarchy(planningItems, selectedProjectId) : [];

  function getPlanningItemCompletion(planningItemId: string): { done: number; total: number; percent: number } {
    const linked = projectTaskItems.filter((i) => i.planning_item_id === planningItemId);
    const total = linked.length;
    const done =
      doneColumnId && total > 0
        ? linked.filter((i) => i.column_id === doneColumnId).length
        : 0;
    const percent = total > 0 ? Math.round((done / total) * 100) : 0;
    return { done, total, percent };
  }

  return (
    <div className="max-w-7xl mx-auto w-full flex-1 min-h-0 flex flex-col overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 shrink-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Project monitoring</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Track overall and per-item completion per project, based on planning items and task tracker Completed items
          </p>
        </div>
      </div>

      <div className="mb-4 shrink-0">
        <label className="block text-sm font-medium text-gray-700 mb-1">Select Project</label>
        <select
          value={selectedProjectId}
          onChange={(e) => setSelectedProjectId(e.target.value)}
          className="w-full max-w-md rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none text-sm appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2024%2024%22%20stroke%3D%22%236b7280%22%3E%3Cpath%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20stroke-width%3D%222%22%20d%3D%22M19%209l-7%207-7-7%22%20%2F%3E%3C%2Fsvg%3E')] bg-[length:1.25rem] bg-[right_0.5rem_center] bg-no-repeat pr-10"
        >
          <option value="">— Choose a project —</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.project_name}
              {p.client_name ? ` (${p.client_name})` : ''}
            </option>
          ))}
        </select>
        {loading && (
          <p className="text-sm text-gray-500 mt-2">Loading projects...</p>
        )}
        {!loading && projects.length === 0 && (
          <p className="text-sm text-amber-600 mt-2">No projects yet. Create a project first in All Projects.</p>
        )}
      </div>

      {selectedProjectId && selectedProject && (
        <>
          {/* Overall completion */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 shrink-0">
            <div className="bg-white rounded-xl p-5 shadow-card border border-gray-100">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Project</p>
              <p className="text-lg font-bold text-gray-900 mt-1 truncate" title={selectedProject.project_name}>
                {selectedProject.project_name}
              </p>
            </div>
            <div className="bg-white rounded-xl p-5 shadow-card border border-gray-100">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Tasks (Completed / Total)</p>
              <p className="text-lg font-bold text-gray-900 mt-1">
                {projectDoneCount} / {projectTaskItems.length}
              </p>
            </div>
            <div className="bg-white rounded-xl p-5 shadow-card border border-gray-100">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Overall completion</p>
              <div className="flex items-center gap-3 mt-1">
                <div className="flex-1 h-3 rounded-full bg-gray-200 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-brand-500 transition-all duration-500"
                    style={{ width: `${overallPercent}%` }}
                  />
                </div>
                <span className="text-lg font-bold text-gray-900 shrink-0">{overallPercent}%</span>
              </div>
            </div>
          </div>

          {/* Per-item completion (planning items = items defined by planning of days) */}
          <h2 className="font-semibold text-gray-900 mb-3 shrink-0">Completion by planning item</h2>
          <p className="text-sm text-gray-500 mb-4 shrink-0">
            Items are from Project Planning (by days). Completion is based on Task Tracker tasks linked to each item and moved to Completed.
          </p>
          <div className="flex-1 min-h-0 overflow-auto bg-white rounded-xl shadow-card border border-gray-100">
            {hierarchy.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                  <svg
                    className="w-8 h-8 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                    />
                  </svg>
                </div>
                <p className="font-medium text-gray-700">No planning items</p>
                <p className="text-sm text-gray-500 mt-1">
                  Add planning items in <strong>Project Planning</strong> to see per-item completion here.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px]">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50/80">
                      <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                        Planning item
                      </th>
                      <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                        Level
                      </th>
                      <th className="text-right text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                        Duration (days)
                      </th>
                      <th className="text-right text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                        Tasks (Completed / Total)
                      </th>
                      <th className="text-right text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                        Item completion
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {hierarchy.map((item) => {
                      const level = getItemLevel(item, planningItems);
                      const levelLabel = level === 1 ? 'Parent' : level === 2 ? 'Child' : 'Mid-level';
                      const indent = level > 1 ? (level - 1) * 20 : 0;
                      const { done, total, percent } = getPlanningItemCompletion(item.id);
                      return (
                        <tr key={item.id} className="hover:bg-gray-50/50 transition-colors">
                          <td className="py-3 px-4">
                            <div style={{ paddingLeft: indent }} className="flex items-center gap-2">
                              {level > 1 && (
                                <span className="text-gray-400">{level === 2 ? '↳' : '↳↳'}</span>
                              )}
                              <span className="font-medium text-gray-900">{item.name}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span
                              className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                                level === 1
                                  ? 'bg-blue-100 text-blue-700'
                                  : level === 2
                                    ? 'bg-sky-100 text-sky-700'
                                    : 'bg-gray-100 text-gray-700'
                              }`}
                            >
                              {levelLabel}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right font-medium text-gray-900">
                            {item.duration_days}
                          </td>
                          <td className="py-3 px-4 text-right font-medium text-gray-900">
                            {done} / {total}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <div className="w-20 h-2 rounded-full bg-gray-200 overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all ${
                                    percent === 100 ? 'bg-emerald-500' : 'bg-brand-500'
                                  }`}
                                  style={{ width: `${percent}%` }}
                                />
                              </div>
                              <span className="font-medium text-gray-900 w-10">{percent}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
